/**
 * Generic public-ArcGIS feature detail panel (#213). Renders any non-evacuation
 * arcgis_public_features category (fire bans, drought, boil-water, State of Local
 * Emergency, tropical cyclones) from its `ArcgisCategoryConfig` — the category's
 * accent colour, layer label, and register palette drive a category-specific panel
 * off ONE component (the evacuation category keeps its bespoke EvacuationOrderBody
 * with population/homes cards).
 *
 * A raw ingested operational signal — authoritative CONTEXT, not an actionable
 * incident. Headlines the feature name + register (the palette label, always paired
 * with the colour, §3A.7), with hazard/severity, issuing agency, and an OGL-Canada
 * attribution footer. Affected population/homes are shown only when the feed carries
 * them (most of these categories don't).
 */
import { useEffect, useState } from 'react';
import type { ArcgisFeature, ArcgisFeatureDetail } from '@tsr/shared-types';
import { fetchArcgisFeatureDetail } from './api';
import { arcgisConfigForCategory } from './layers/arcgisCategories';
import { logAction } from './lib/operatorActions';

function agoLabel(iso: string | null): string {
  if (!iso) return 'unknown';
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms)) return 'unknown';
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function whenLabel(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleString()} · ${agoLabel(iso)}`;
}

function num(n: number | null | undefined): string {
  return n == null ? '—' : n.toLocaleString();
}

export function ArcgisFeatureBody({ feature }: { feature: ArcgisFeature }): JSX.Element {
  const p = feature.properties;
  const config = arcgisConfigForCategory(p.category);
  const style = config?.style(p);
  const accent = style?.color ?? config?.accentColor ?? '#9ca3af';
  const registerLabel = style?.label ?? p.severity ?? config?.label ?? 'Feature';
  const [detail, setDetail] = useState<ArcgisFeatureDetail | null>(null);

  useEffect(() => {
    logAction(config?.auditDetailVerb ?? 'view_arcgis_feature_detail', {
      feature_id: feature.id,
      category: p.category,
      severity: p.severity,
    });
  }, [feature.id, p.category, p.severity, config?.auditDetailVerb]);

  useEffect(() => {
    const ctl = new AbortController();
    void fetchArcgisFeatureDetail(feature.id, ctl.signal)
      .then(setDetail)
      .catch(() => undefined);
    return () => ctl.abort();
  }, [feature.id]);

  const showPopHomes = p.population != null || p.homes != null;

  return (
    <div className="px-4 py-4 space-y-4" data-testid="arcgis-feature-body">
      <div className="border-l-2 pl-3" style={{ borderColor: accent }}>
        <p className="text-[10px] uppercase tracking-wider text-slate-500">
          {config?.label ?? p.category}
        </p>
        <h2 className="text-base font-semibold">{p.name || 'Unnamed feature'}</h2>
        <span
          className="inline-flex items-center gap-1.5 mt-1.5 text-[11px] px-2 py-0.5 rounded font-medium"
          style={{ backgroundColor: `${accent}22`, color: accent }}
        >
          <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: accent }} />
          {registerLabel}
        </span>
      </div>

      {showPopHomes && (
        <section className="grid grid-cols-2 gap-2">
          <div className="rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Population</p>
            <p className="text-xl font-semibold tabular-nums">{num(p.population)}</p>
          </div>
          <div className="rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Homes</p>
            <p className="text-xl font-semibold tabular-nums">{num(p.homes)}</p>
          </div>
        </section>
      )}

      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Details</h3>
        <dl className="grid grid-cols-[8rem_1fr] gap-y-1 text-sm">
          <dt className="text-slate-400">Type</dt>
          <dd>{p.event_type || '—'}</dd>
          <dt className="text-slate-400">Register</dt>
          <dd>{p.severity || '—'}</dd>
          <dt className="text-slate-400">Status</dt>
          <dd className="capitalize">{p.status}</dd>
          {detail?.properties.issuing_agency && (
            <>
              <dt className="text-slate-400">Issuing agency</dt>
              <dd>{detail.properties.issuing_agency}</dd>
            </>
          )}
          {detail?.properties.event_start_at && (
            <>
              <dt className="text-slate-400">Effective</dt>
              <dd className="text-xs">{whenLabel(detail.properties.event_start_at)}</dd>
            </>
          )}
          <dt className="text-slate-400">Updated</dt>
          <dd className="text-xs">{whenLabel(p.observed_at)}</dd>
          <dt className="text-slate-400">Centroid</dt>
          <dd className="font-mono text-xs">
            {p.lat.toFixed(3)}, {p.lon.toFixed(3)}
          </dd>
        </dl>
      </section>

      <p className="text-[11px] text-slate-400 leading-relaxed">
        Drawn on the map as an ambient {config?.label.toLowerCase() ?? 'advisory'} layer (visible at
        regional zoom). Verify the current status with the issuing authority before acting.
      </p>

      <footer className="border-t border-slate-800 pt-3 text-[10px] text-slate-500 leading-relaxed space-y-0.5">
        {detail?.properties.ingested_at && <div>Ingested {agoLabel(detail.properties.ingested_at)}</div>}
        {detail?.properties.source_url && (
          <div className="truncate">
            Source:{' '}
            <a
              href={detail.properties.source_url}
              target="_blank"
              rel="noreferrer"
              className="underline hover:text-slate-300"
            >
              {detail.properties.feed_key} FeatureServer
            </a>
          </div>
        )}
        <div>{p.attribution}</div>
      </footer>
    </div>
  );
}
